import React, { useState, useEffect, useCallback, useMemo, Suspense, lazy } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { adminLogin, getGuests, getMessageStats, getDeletedGuests, importGuests, updateGuest, adminAddGuest as addGuest, softDeleteGuest as deleteGuest, clearAdminSession, LOGO_URL, LOGO_URL_SMALL, LOGO_URL_ICON } from '../../data/api';
import { theme } from '../../data/colors';
import { Trash2, LogOut, Users, Send, BarChart3, MessageCircle, Upload, Settings, Shield, AlertTriangle, Building2, Calendar, LayoutDashboard, Menu, X, Plus, MoreHorizontal, Bell, ChevronDown, ChevronLeft, ChevronRight, Edit3, Check, FileText, Utensils, Grid3X3, Mail, ClipboardList, PieChart, Search, HelpCircle, Sparkles, Compass, Video } from 'lucide-react';
import GuestListTab from './GuestListTab';
import SendNoticesTab from './SendNoticesTab';
import SentHistoryTab from './SentHistoryTab';
import MessagingTab from './MessagingTab';
import WebCommentsTab from './WebCommentsTab';
import AITab from './AITab';
import UploadTab from './UploadTab';
import SettingsTab from './SettingsTab';
import DataProtectionTab from './DataProtectionTab';
import VendorsTab from './VendorsTab';
import AttentionTab from './AttentionTab';
import FinanceTab from './FinanceTab';
// CalendarTab.jsx kept in repo as rollback/reference; Calendar Intelligence is the default render.
import CalendarIntelligenceTab from './calendar-intelligence/CalendarIntelligenceTab';
import AnalyticsTab from './AnalyticsTab';
import TrashModal from './TrashModal';
import DashboardModals from './DashboardModals';
import GuestMergeModal from './GuestMergeModal';
import DashboardTab from './DashboardTab';
import HostDashboardQrButton from './HostDashboardQrButton';
import CommunicationsHub, { isCommunicationsHubEnabled } from './communications';
// NEW, flagged Communications Intelligence module (default OFF). Additive import
// only — the live hub above is untouched.
import CommunicationsIntelligence, { isCommunicationsIntelligenceEnabled } from './communications-intelligence';
import EventReadinessPage, { isEventReadinessEnabled, AskMiloPalette, QuickAddComposer } from './event-readiness';
// NEW, flagged Event Program module (day-of run-of-show, default OFF). Additive.
import EventProgramPage, { isEventProgramEnabled } from './event-program';
// NEW, flagged Auto Seating module (day-of seating planner, default OFF). Additive.
import AutoSeatingPage, { isAutoSeatingEnabled } from './auto-seating';
// NEW, flagged Intelligence Notebook module (free-form capture → Milo orbs, default OFF). Additive.
// Lazy-split so `motion` + the notebook only load when the flag is on and the tab
// is opened — production (flag OFF) never ships this chunk in the main bundle.
import { isIntelligenceNotebookEnabled } from './intelligence-notebook/intelligenceNotebookFlag';
const IntelligenceNotebookPage = lazy(() => import('./intelligence-notebook/IntelligenceNotebookPage'));
// NEW, flagged Video Guestbook admin gallery (Oscar messages, default OFF). Additive.
// Lazy-split so it only loads when the flag is on and the tab is opened.
import { isVideoGuestbookEnabled } from './video-guestbook/videoGuestbookFlag';
const VideoGuestbookAdmin = lazy(() => import('./video-guestbook/VideoGuestbookAdmin'));
import { createOperation as apiCreateOperation, parseQuickAdd as apiParseQuickAdd } from '../../data/api';
import { AdminToaster, showSaveSuccess, showSaveError, showLocalOnlyInfo } from './AdminToaster';
import { MOBILE_LIGHT_ENABLED } from './mobile';
import './adminStyles.css';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// ==============================================
// GOLD COLOR SYSTEM - Now references theme from colors.js
// ==============================================
const GOLD = {
  highlight: '#E6C579',
  light: '#D4B872',
  primary: theme.accentPrimary,
  deep: '#9F8B5D',
  dark: '#8A7847',
  stitch: '#8A7847',
};

const COLORS = {
  gold: theme.accentPrimary,
  cream: '#F3EEE2',
  creamDark: '#EBE3D1',
  white: theme.textInverse,
  grey: theme.textMuted,
  greyLight: '#E2DCCF',
  greyDark: '#463F34',
  black: theme.textPrimary,
  error: theme.btnDestructiveBg,
  success: theme.statusSuccess,
  warning: theme.statusWarning,
};

const goldGradient = `linear-gradient(135deg, #E6C579 0%, #D4B872 25%, #C6A75A 50%, #9F8B5D 75%, #8A7847 100%)`;
const goldGradientShine = `linear-gradient(135deg, #F0D890 0%, #E6C579 15%, #D4B872 35%, #C6A75A 55%, #9F8B5D 75%, #8A7847 100%)`;

// ==============================================
// SIDEBAR NAVIGATION - Grouped like v1.1-admin-uplift
// ==============================================
const navGroups = {
  core: {
    label: 'Core',
    items: [
      { id: 'event-readiness', label: 'Event Readiness', icon: 'compass-outline', lucideIcon: Compass },
      { id: 'dashboard', label: 'Dashboard', icon: 'grid-outline', lucideIcon: LayoutDashboard },
      { id: 'guests', label: 'Guests', icon: 'people-outline', lucideIcon: Users, badge: true },
      { id: 'messaging', label: 'Messaging', icon: 'mail-outline', lucideIcon: Mail, badge: true },
      { id: 'mailbox', label: 'Guest Mailbox', icon: 'chatbox-ellipses-outline', lucideIcon: MessageCircle },
      { id: 'vendors', label: 'Vendors', icon: 'storefront-outline', lucideIcon: Building2 },
      { id: 'finance', label: 'Finance', icon: 'card-outline', lucideIcon: PieChart },
    ],
  },
  planning: {
    label: 'Planning',
    items: [
      { id: 'calendar', label: 'Calendar', icon: 'calendar-outline', lucideIcon: Calendar },
      { id: 'seating', label: 'Seating', icon: 'grid-outline', lucideIcon: Grid3X3 },
      { id: 'event-program', label: 'Event Program', icon: 'list-outline', lucideIcon: ClipboardList },
      { id: 'analytics', label: 'Analytics', icon: 'bar-chart-outline', lucideIcon: BarChart3 },
      { id: 'oscar', label: 'AI', icon: 'sparkles-outline', lucideIcon: Sparkles },
    ],
  },
  control: {
    label: 'Control',
    items: [
      { id: 'security', label: 'Security', icon: 'shield-outline', lucideIcon: Shield },
      { id: 'settings', label: 'Settings', icon: 'settings-outline', lucideIcon: Settings },
      { id: 'trash', label: 'Trash', icon: 'trash-outline', lucideIcon: Trash2 },
    ],
  },
};

// ==============================================
// SIDEBAR NAVIGATION — Intelligence Notebook spec (flag ON only)
// Exact host spec: CORE / PLANNING / INTELLIGENCE / CONTROL with the new
// Intelligence Notebook tab under PLANNING. Used ONLY when the notebook flag is
// enabled, so production nav (navGroups above) stays untouched until deploy.
// ==============================================
const navGroupsNotebook = {
  core: {
    label: 'Core',
    items: [
      { id: 'event-readiness', label: 'Event Readiness', icon: 'compass-outline', lucideIcon: Compass },
      { id: 'dashboard', label: 'Dashboard', icon: 'grid-outline', lucideIcon: LayoutDashboard },
      { id: 'guests', label: 'Guests', icon: 'people-outline', lucideIcon: Users, badge: true },
      { id: 'messaging', label: 'Communications', icon: 'mail-outline', lucideIcon: Mail, badge: true },
      { id: 'vendors', label: 'Vendors', icon: 'storefront-outline', lucideIcon: Building2 },
      { id: 'finance', label: 'Finance', icon: 'card-outline', lucideIcon: PieChart },
    ],
  },
  planning: {
    label: 'Planning',
    items: [
      { id: 'calendar', label: 'Calendar', icon: 'calendar-outline', lucideIcon: Calendar },
      { id: 'seating', label: 'Seating', icon: 'grid-outline', lucideIcon: Grid3X3 },
      { id: 'event-program', label: 'Event Program', icon: 'list-outline', lucideIcon: ClipboardList },
      { id: 'intelligence-notebook', label: 'Intelligence Notebook', icon: 'book-outline', lucideIcon: FileText },
      { id: 'video-guestbook', label: 'Video Messages', icon: 'videocam-outline', lucideIcon: Video },
    ],
  },
  intelligence: {
    label: 'Intelligence',
    items: [
      { id: 'analytics', label: 'Analytics', icon: 'bar-chart-outline', lucideIcon: BarChart3 },
      { id: 'oscar', label: 'AI', icon: 'sparkles-outline', lucideIcon: Sparkles },
    ],
  },
  control: {
    label: 'Control',
    items: [
      { id: 'security', label: 'Security', icon: 'shield-outline', lucideIcon: Shield },
      { id: 'settings', label: 'Settings', icon: 'settings-outline', lucideIcon: Settings },
      { id: 'trash', label: 'Trash', icon: 'trash-outline', lucideIcon: Trash2 },
    ],
  },
};

// ==============================================
// DESKTOP SIDEBAR ITEMS - All tabs included
// ==============================================
const desktopSidebarItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'guests', label: 'Guests', icon: Utensils },
  { id: 'parties', label: 'Parties', icon: Grid3X3 },
  { id: 'vendors', label: 'Vendors', icon: Building2 },
  { id: 'rsvp', label: 'RSVP', icon: Edit3 },
  { id: 'tasks', label: 'Tasks', icon: ClipboardList },
  { id: 'analytics', label: 'Analytics', icon: PieChart },
  { id: 'messages', label: 'Messages', icon: Send },
  { id: 'upload', label: 'Upload', icon: Upload },
  { id: 'storage', label: 'Storage', icon: Shield },
  { id: 'settings', label: 'Settings', icon: Settings },
];

// Keep old items for mobile and other tabs
const allSidebarItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'guests', label: 'Guests', icon: Users },
  { id: 'calendar', label: 'Calendar', icon: Calendar },
  { id: 'alerts', label: 'Alerts', icon: AlertTriangle },
  { id: 'notices', label: 'Send Notices', icon: Send },
  { id: 'protection', label: 'Data Protection', icon: Shield },
  { id: 'vendors', label: 'Vendors', icon: Building2 },
  { id: 'history', label: 'Sent History', icon: BarChart3 },
  { id: 'comments', label: 'Web Comments', icon: MessageCircle },
  { id: 'upload', label: 'Upload', icon: Upload },
  { id: 'settings', label: 'Settings', icon: Settings },
];

// Mobile nav items
const mobileNavItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'guests', label: 'Guests', icon: Users },
  { id: 'vendors', label: 'Vendytics', icon: Building2 },
];

// Tab mapping for sidebar items to actual tab content
const tabMapping = {
  'dashboard': 'dashboard',
  'guests': 'guests',
  'parties': 'calendar',
  'vendors': 'vendors',
  'rsvp': 'guests',
  'tasks': 'attention',
  'analytics': 'analytics',
  'messages': 'notices',
  'upload': 'upload',
  'storage': 'protection',
  'settings': 'settings',
};

// ==============================================
// BACKGROUND STYLES - Quilted Textures
// ==============================================

// Import quilted texture
const quiltedTextureWhite = require('../../assets/quilted-texture-white.png');

// White/gold background for admin page — replaces gray quilted texture
const globalQuiltedBgStyle = {
  backgroundImage: `url(/admin-bg.png)`,
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
  backgroundAttachment: 'fixed',
  backgroundPosition: 'center center',
  backgroundColor: '#F7F3EC',
};

// Black quilted leather texture for sidebar (CSS-based diamond pattern)
const blackQuiltedLeatherStyle = {
  backgroundColor: '#0A0A0B',
  backgroundImage: `
    linear-gradient(135deg, rgba(30, 30, 30, 0.9) 25%, transparent 25%),
    linear-gradient(225deg, rgba(30, 30, 30, 0.9) 25%, transparent 25%),
    linear-gradient(45deg, rgba(30, 30, 30, 0.9) 25%, transparent 25%),
    linear-gradient(315deg, rgba(30, 30, 30, 0.9) 25%, transparent 25%),
    linear-gradient(to bottom, rgba(20, 20, 20, 0.5) 0%, rgba(10, 10, 10, 0.8) 50%, rgba(20, 20, 20, 0.5) 100%)
  `,
  backgroundSize: '40px 40px, 40px 40px, 40px 40px, 40px 40px, 100% 100%',
  backgroundPosition: '0 0, 20px 0, 20px -20px, 0 20px, 0 0',
};

// Legacy aliases - all transparent to let root background show through
const whiteQuiltedBgStyle = { backgroundColor: 'transparent' };
const stitchedBgStyle = { backgroundColor: 'transparent' };
const whiteBannerStyle = { backgroundColor: 'transparent', boxShadow: 'none' };

// ==============================================
// STYLE COMPONENTS
// ==============================================

const GoldDustOverlay = ({ intense = false }) => (
  <div style={{
    position: 'absolute',
    inset: 0,
    borderRadius: 'inherit',
    pointerEvents: 'none',
    zIndex: 1,
    background: intense ? `
      radial-gradient(circle at 15% 85%, rgba(230, 197, 121, 0.25) 0%, transparent 25%),
      radial-gradient(circle at 85% 15%, rgba(212, 170, 105, 0.22) 0%, transparent 22%),
      radial-gradient(circle at 50% 50%, rgba(200, 160, 76, 0.15) 0%, transparent 30%),
      radial-gradient(circle at 25% 25%, rgba(240, 216, 144, 0.18) 0%, transparent 18%),
      radial-gradient(circle at 75% 75%, rgba(212, 170, 105, 0.20) 0%, transparent 20%),
      radial-gradient(circle at 40% 70%, rgba(184, 148, 47, 0.12) 0%, transparent 15%),
      radial-gradient(circle at 60% 30%, rgba(230, 197, 121, 0.14) 0%, transparent 16%)
    ` : `
      radial-gradient(circle at 20% 80%, rgba(212, 170, 105, 0.18) 0%, transparent 30%),
      radial-gradient(circle at 80% 20%, rgba(200, 160, 76, 0.15) 0%, transparent 25%),
      radial-gradient(circle at 60% 60%, rgba(184, 148, 47, 0.12) 0%, transparent 22%),
      radial-gradient(circle at 30% 30%, rgba(230, 197, 121, 0.10) 0%, transparent 18%),
      radial-gradient(circle at 70% 70%, rgba(212, 170, 105, 0.14) 0%, transparent 20%)
    `,
  }} />
);

const NoiseOverlay = () => (
  <div style={{
    position: 'absolute',
    inset: 0,
    borderRadius: 'inherit',
    pointerEvents: 'none',
    zIndex: 1,
    opacity: 0.04,
    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
  }} />
);

const GoldBadge = ({ count, large = false }) => (
  <span style={{
    background: goldGradientShine,
    color: '#1A1A1A',
    fontSize: large ? 14 : 12,
    fontWeight: 700,
    padding: large ? '4px 14px' : '3px 12px',
    borderRadius: 12,
    boxShadow: '0 2px 6px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.4)',
    marginLeft: 8,
  }}>{count}</span>
);

const GoldProgressBar = ({ percent, height = 10, dark = false }) => (
  <div style={{
    background: dark ? '#2A2A2A' : 'rgba(0,0,0,0.1)',
    borderRadius: 5,
    height,
    overflow: 'hidden',
    boxShadow: dark ? 'inset 0 2px 4px rgba(0,0,0,0.5)' : 'inset 0 1px 3px rgba(0,0,0,0.15)',
  }}>
    <div style={{
      background: `linear-gradient(180deg, rgba(255,255,255,0.3) 0%, transparent 50%), linear-gradient(90deg, #f0d890 0%, #e6c579 20%, #d4aa69 40%, #c8a04c 60%, #b8942f 80%, #9a7b25 100%)`,
      height: '100%',
      width: `${Math.min(percent, 100)}%`,
      borderRadius: 5,
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.4), inset 0 -1px 0 rgba(0,0,0,0.2), 0 0 4px rgba(212,170,105,0.3)',
    }} />
  </div>
);

const whiteTileStyle = {
  background: theme.bgCard,
  borderRadius: 10,
  border: '1px solid rgba(200, 195, 180, 0.5)',
  boxShadow: '0 6px 16px rgba(0,0,0,0.1), 0 1px 0 rgba(255,255,255,0.8) inset',
};

const darkPanelStyle = {
  background: 'linear-gradient(180deg, #1E1E1E 0%, #1A1A1A 50%, #151515 100%)',
  borderRadius: 10,
  border: '1px solid rgba(80, 80, 80, 0.3)',
  boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
  position: 'relative',
  overflow: 'hidden',
};

// ==============================================
// RSVP PROGRESS RING COMPONENT - From attachment 4
// ==============================================
const RSVPProgressRing = ({ yes, pending, no }) => {
  const total = yes + pending + no;
  const yesPercent = total > 0 ? (yes / total) * 100 : 0;
  const pendingPercent = total > 0 ? (pending / total) * 100 : 0;
  const responseRate = total > 0 ? Math.round(((yes + no) / total) * 100) : 0;
  
  // Calculate stroke dash for SVG ring
  const radius = 70;
  const circumference = 2 * Math.PI * radius;
  const yesStroke = (yesPercent / 100) * circumference;
  const pendingStroke = (pendingPercent / 100) * circumference;
  
  return (
    <div style={{ ...darkPanelStyle, padding: 28 }}>
      <GoldDustOverlay intense />
      <NoiseOverlay />
      <div style={{ position: 'relative', zIndex: 2 }}>
        <div style={{ fontSize: 18, fontWeight: 600, color: '#fff', marginBottom: 24 }}>RSVP Progress</div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: 40 }}>
          {/* Circular Progress Ring */}
          <div style={{ position: 'relative', width: 160, height: 160 }}>
            <svg width="160" height="160" style={{ transform: 'rotate(-90deg)' }}>
              {/* Background ring */}
              <circle
                cx="80"
                cy="80"
                r={radius}
                fill="none"
                stroke="#333"
                strokeWidth="12"
              />
              {/* Gold/Pending segment */}
              <circle
                cx="80"
                cy="80"
                r={radius}
                fill="none"
                stroke={GOLD.highlight}
                strokeWidth="12"
                strokeDasharray={`${pendingStroke} ${circumference}`}
                strokeDashoffset={-yesStroke}
                strokeLinecap="round"
              />
              {/* Green/Yes segment */}
              <circle
                cx="80"
                cy="80"
                r={radius}
                fill="none"
                stroke="#4CAF50"
                strokeWidth="12"
                strokeDasharray={`${yesStroke} ${circumference}`}
                strokeLinecap="round"
                style={{ filter: 'drop-shadow(0 0 6px rgba(76, 175, 80, 0.5))' }}
              />
              {/* Outer gold ring */}
              <circle
                cx="80"
                cy="80"
                r="78"
                fill="none"
                stroke={GOLD.dark}
                strokeWidth="2"
              />
            </svg>
            {/* Center text */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: 36, fontWeight: 700, color: '#fff' }}>{responseRate}%</div>
              <div style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>Responsive</div>
            </div>
            {/* Green glow indicator */}
            <div style={{
              position: 'absolute',
              left: -4,
              top: '50%',
              width: 8,
              height: 40,
              background: 'linear-gradient(180deg, transparent, #4CAF50, transparent)',
              filter: 'blur(4px)',
              transform: 'translateY(-50%)',
            }} />
          </div>
          
          {/* Legend */}
          <div style={{ flex: 1 }}>
            {[
              { label: 'Yes', value: yes, color: theme.statusSuccess },
              { label: 'Pending', value: pending, color: GOLD.highlight },
              { label: 'No', value: no, color: theme.statusError },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
                <div style={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  background: item.color,
                  boxShadow: `0 0 8px ${item.color}`,
                }} />
                <span style={{ fontSize: 16, color: '#ccc', width: 70 }}>{item.label}</span>
                <span style={{ fontSize: 20, fontWeight: 700, color: '#fff' }}>{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Time-of-day greeting for the header (matches erHelpers.greetingWord): morning / afternoon /
// evening / "Working late" after 10pm. Personalized with the host's first name from settings.
const adminGreetingWord = (date = new Date()) => {
  const h = date.getHours();
  if (h >= 5 && h < 12) return 'Good morning';
  if (h >= 12 && h < 17) return 'Good afternoon';
  if (h >= 17 && h < 22) return 'Good evening';
  return 'Working late';
};
const firstNameOf = (full) => (String(full || '').trim().split(/\s+/)[0] || '');
const lastNameOf = (full) => {
  // Strip a trailing "Family"/"Household"/etc. so "Valenciano Family" -> "Valenciano"
  // (not "Family"); plain "First Last" still -> "Last".
  const cleaned = String(full || '').trim().replace(/\s+(family|household|residence|home)\.?$/i, '').trim();
  const p = cleaned.split(/\s+/).filter(Boolean);
  return p.length ? p[p.length - 1] : '';
};
// Premium greeting name: "Mr. Valenciano" when a salutation is set in Settings; else the
// first name (for multi-word names); else the name as stored. Settings-driven, graceful.
const hostGreetingName = (cfg = {}) => {
  const sal = String(cfg.host_salutation || '').trim();
  const name = String(cfg.host_name || '').trim();
  if (!name) return '';
  if (sal) return `${sal} ${lastNameOf(name)}`.trim();
  const parts = name.split(/\s+/).filter(Boolean);
  return parts.length > 1 ? parts[0] : name;
};

// ==============================================
// MAIN COMPONENT
// ==============================================
const AdminPage = () => {
  const communicationsHubEnabled = isCommunicationsHubEnabled();
  // NEW, flagged: when ON, the 'communications' tab renders Communications
  // Intelligence instead of the live hub. Default OFF — nothing changes unless
  // localStorage 'ci_enabled'==='1' or REACT_APP_COMMS_INTELLIGENCE==='true'.
  const commsIntelligenceEnabled = isCommunicationsIntelligenceEnabled();
  const eventReadinessEnabled = isEventReadinessEnabled();
  const eventProgramEnabled = isEventProgramEnabled();
  const autoSeatingEnabled = isAutoSeatingEnabled();
  const intelligenceNotebookEnabled = isIntelligenceNotebookEnabled();
  const videoGuestbookEnabled = isVideoGuestbookEnabled();
  const [authed, setAuthed] = useState(!!sessionStorage.getItem('admin_authed'));
  const [pw, setPw] = useState('');
  const [pwErr, setPwErr] = useState('');
  const [guests, setGuests] = useState([]);
  const [dashStats, setDashStats] = useState(null);
  const [dashStatsCheckedAt, setDashStatsCheckedAt] = useState(null);
  const [deletedCount, setDeletedCount] = useState(0);
  const [officialInvitationsSent, setOfficialInvitationsSent] = useState(0);
  // Event Readiness is the default landing tab post-login when enabled
  // (v6.2 production wire — Dashboard remains accessible as a separate tab).
  const [activeTab, setActiveTab] = useState(eventReadinessEnabled ? 'event-readiness' : 'dashboard');
  // Phase 2 — bidirectional operation ↔ module navigation. Lifted here so
  // EventReadinessPage can deep-link into Vendors/Finance/etc., and module
  // pages can deep-link back into Event Readiness with a focused row.
  const [focusEntity, setFocusEntity] = useState(null); // { module, entity_id?, op_id? }
  const [messagingSubtab, setMessagingSubtab] = useState(null);
  const [showTrash, setShowTrash] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [activeModal, setActiveModal] = useState(null);
  const [mergeGuests, setMergeGuests] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [alertsData, setAlertsData] = useState([]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Phone access fix: the legacy mobile layout (<768px) only exposed an old, partial
  // nav (Dashboard/Guests/Vendors + a stale "More" list) and could not render the newer
  // modules. Render the full layout on all widths so every module is reachable on phones.
  // (Desktop layout is responsive enough to use on a phone with scroll/zoom.)
  const [isMobile, setIsMobile] = useState(false);
  
  // State for new Dashboard component
  const [vendors, setVendors] = useState([]);
  const [messages, setMessages] = useState([]);
  const [dashboardBudget, setDashboardBudget] = useState(null);
  const [sponsors, setSponsors] = useState([]);
  
  // Finance entries state - NOW FETCHED FROM BACKEND (no more sessionStorage)
  const [financeEntries, setFinanceEntries] = useState([]);
  
  // Fetch finance entries from backend
  const fetchFinanceEntries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/finance/entries`);
      setFinanceEntries(res.data || []);
    } catch (err) {
      console.error('Failed to fetch finance entries:', err);
    }
  }, []);
  
  // Fetch finance entries on mount
  useEffect(() => {
    fetchFinanceEntries();
  }, [fetchFinanceEntries]);
  
  // Refresh finance entries when switching to Calendar tab (to get latest data)
  useEffect(() => {
    if (activeTab === 'calendar') {
      fetchFinanceEntries();
    }
  }, [activeTab, fetchFinanceEntries]);

  // "View in Notebook" from a surfaced Milo item (any destination tab) → jump to the
  // Intelligence Notebook tab. Gated; only active when the notebook flag is on.
  useEffect(() => {
    if (!intelligenceNotebookEnabled) return undefined;
    const onOpen = () => setActiveTab('intelligence-notebook');
    window.addEventListener('open-intelligence-notebook', onOpen);
    return () => window.removeEventListener('open-intelligence-notebook', onOpen);
  }, [intelligenceNotebookEnabled]);

  // "Email roster" from Seating → jump to the Communications tab. The Communications Hub reads the
  // one-shot prefill (subject + roster body) from sessionStorage on mount and opens its composer.
  useEffect(() => {
    const onCompose = () => setActiveTab('communications');
    window.addEventListener('open-communications-compose', onCompose);
    return () => window.removeEventListener('open-communications-compose', onCompose);
  }, []);

  // Reset messagingSubtab when user navigates away from messaging.
  // Milo proposals can set messagingSubtab when routing to Messaging
  // (e.g. 'compose', 'email'). When the user later leaves the Messaging
  // tab, that hint should not persist — the next manual return to
  // Messaging should land on its default subtab. Without this reset,
  // a Milo-set subtab leaks across navigations.
  useEffect(() => {
    if (activeTab !== 'messaging') {
      setMessagingSubtab(null);
    }
  }, [activeTab]);
  
  // Editable dashboard title
  const [dashboardTitle, setDashboardTitle] = useState('80 Years of Connection');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [tempTitle, setTempTitle] = useState(dashboardTitle);
  
  // Desktop layout state - must be at top level (not inside conditionals)
  const [collapsedGroups, setCollapsedGroups] = useState({ core: false, planning: false, control: false });
  const [searchQuery, setSearchQuery] = useState('');
  // Guest to open on search-result click
  const [focusGuestId, setFocusGuestId] = useState(null);
  // v6.3 Quick Add: global ⌘⇧A / Ctrl+Shift+A opens a modal Quick Add composer
  // that captures an Operation from anywhere in admin. The Operations Queue
  // refreshes on next mount because it re-fetches operations via useOperations.
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifItems, setNotifItems] = useState([]);
  
  // Live date/time for header
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute
    return () => clearInterval(timer);
  }, []);

  // v6.3 Quick Add — global keyboard shortcut. ⌘⇧A on macOS, Ctrl+Shift+A on
  // Win/Linux. Captured at the document level so it works on every admin tab.
  // Ignored when focus is inside an input/textarea so it doesn't intercept
  // user typing. Esc inside the modal closes (the composer itself handles it).
  useEffect(() => {
    if (!authed || !eventReadinessEnabled) return undefined;
    const handler = (e) => {
      const isCmdShiftA = (e.metaKey || e.ctrlKey) && e.shiftKey && (e.key === 'a' || e.key === 'A');
      if (!isCmdShiftA) return;
      const t = e.target;
      const tag = t && t.tagName ? t.tagName.toLowerCase() : '';
      if (tag === 'input' || tag === 'textarea' || (t && t.isContentEditable)) return;
      e.preventDefault();
      setQuickAddOpen(true);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [authed, eventReadinessEnabled]);

  // Close notification dropdown on outside click
  useEffect(() => {
    if (!notifOpen) return;
    const handleClick = (e) => {
      const dropdown = document.querySelector('[data-testid="notif-dropdown"]');
      const btn = document.querySelector('[data-testid="header-notifications-btn"]');
      if (dropdown && !dropdown.contains(e.target) && btn && !btn.contains(e.target)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [notifOpen]);

  
  // Journey config state - persisted to localStorage
  const [journeyConfig, setJourneyConfig] = useState(() => {
    try {
      const saved = localStorage.getItem('cv80_journey_config');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  
  // Event config state - persisted to backend via /api/event/config
  const [eventConfig, setEventConfig] = useState({
    name: '',
    honoree: '',
    date: '2026-07-12',
    location: '',
    capacity: null,
    minimum_guest_target: null,
  });
  
  // Save journey config handler - LOCAL ONLY (backend-dependent)
  const handleSaveJourneyConfig = (config) => {
    setJourneyConfig(config);
    try {
      localStorage.setItem('cv80_journey_config', JSON.stringify(config));
      showLocalOnlyInfo('Journey config saved to this device');
    } catch (e) {
      console.error('Failed to save journey config:', e);
      showSaveError('Failed to save journey config locally');
    }
  };
  
  // Fetch event config from backend on mount
  useEffect(() => {
    const fetchEventConfig = async () => {
      try {
        const res = await axios.get(`${API}/api/event/config`);
        if (res.data) {
          setEventConfig(res.data);
          // Sync header title to persisted event name
          if (res.data.name) setDashboardTitle(res.data.name);
        }
      } catch (e) {
        console.error('Failed to fetch event config:', e);
      }
    };
    if (authed) {
      fetchEventConfig();
    }
  }, [authed]);
  
  // Save event identity handler - persists to backend
  const handleSaveIdentity = async (config) => {
    try {
      const res = await axios.put(`${API}/api/event/config`, config);
      if (res.data?.success && res.data?.config) {
        setEventConfig(res.data.config);
        // Sync header title when event name is saved
        if (res.data.config.name) setDashboardTitle(res.data.config.name);
        showSaveSuccess('Event identity saved');
        return true;
      } else {
        showSaveError('Save failed - unexpected response');
        return false;
      }
    } catch (e) {
      console.error('Failed to save event config:', e);
      showSaveError(e.response?.data?.detail || 'Failed to save event identity');
      return false;
    }
  };
  
  // Compute days until event - uses eventConfig.date as source of truth
  const daysUntilEvent = useMemo(() => {
    const dateStr = eventConfig?.date || '2026-07-26';
    let eventDate;
    
    // Try parsing as ISO format first (YYYY-MM-DD)
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      eventDate = new Date(`${dateStr}T00:00:00`);
    } else {
      // Try parsing human-readable format (e.g., "July 26, 2026")
      eventDate = new Date(dateStr);
    }
    
    const days = Math.max(0, Math.ceil((eventDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
    return isNaN(days) ? 0 : days;
  }, [eventConfig?.date]);

  // ── Global Search Results ──────────────────────────────────────────────────
  const searchResults = useMemo(() => {
    if (!searchQuery || searchQuery.length < 2) return null;
    
    const q = searchQuery.toLowerCase();
    const results = { guests: [], vendors: [], total: 0 };
    
    // Search guests
    results.guests = guests
      .filter(g => 
        (g.first_name || '').toLowerCase().includes(q) ||
        (g.last_name || '').toLowerCase().includes(q) ||
        (g.email || '').toLowerCase().includes(q) ||
        (g.phone || '').includes(q)
      )
      .slice(0, 5);
    
    // Search vendors
    results.vendors = vendors
      .filter(v => 
        (v.company_name || '').toLowerCase().includes(q) ||
        (v.role_category || '').toLowerCase().includes(q) ||
        (v.contact_name || '').toLowerCase().includes(q)
      )
      .slice(0, 5);
    
    results.total = results.guests.length + results.vendors.length;
    return results;
  }, [searchQuery, guests, vendors]);

  useEffect(() => {
    // Full layout on all widths (see isMobile init above) — keep it pinned on resize.
    const handleResize = () => setIsMobile(false);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogin = async () => {
    try {
      const r = await adminLogin(pw);
      if (r.success) {
        sessionStorage.setItem('admin_authed', '1');
        if (r.token) sessionStorage.setItem('admin_token', r.token);
        setAuthed(true);
      } else {
        setPwErr(r.message || 'Invalid password');
      }
    } catch (e) {
      console.error('Login error:', e);
      setPwErr('Error logging in');
    }
  };

  const refreshAll = useCallback(async () => {
    if (!authed) return;
    try {
      const [gr, sr, dr, vr, mr, br, fer, spr, dashStatsRes] = await Promise.all([
        getGuests(), 
        getMessageStats(), 
        getDeletedGuests(),
        axios.get(`${API}/api/vendors`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/messages`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/budget`).catch(() => ({ data: null })),
        axios.get(`${API}/api/finance/entries`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/sponsors`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/dashboard/stats`).catch(() => ({ data: {} })),
      ]);
      setGuests(gr || []);
      setDashStats(dashStatsRes.data);
      setDashStatsCheckedAt(new Date());
      setDeletedCount(Array.isArray(dr) ? dr.length : (dr?.guests?.length || 0));
      setVendors(vr.data || []);
      setMessages(mr.data || []);
      setFinanceEntries(fer.data || []);
      setOfficialInvitationsSent(dashStatsRes.data?.official_invitations_sent ?? 0);
      
      // Calculate dashboard budget with consistent data source
      const budgetData = br.data;
      const financeEntriesData = fer.data || [];
      const sponsorsData = spr.data || [];
      const vendorsData = vr.data || [];
      setSponsors(sponsorsData);
      
      if (budgetData) {
        const totalBudget = budgetData.total_budget || 0;
        const reservePercent = budgetData.reserve_percent || 10;
        const reserveAmount = totalBudget * (reservePercent / 100);
        
        // Calculate spent from finance entries
        const financeSpent = financeEntriesData
          .filter(e => !e.cancelled)
          .reduce((sum, e) => sum + (e.amount_paid || 0), 0);
        
        // Calculate spent from vendors
        const vendorSpent = vendorsData.reduce((sum, v) => sum + (v.paid_to_date || 0), 0);
        
        // Combined spent
        const totalSpent = financeSpent + vendorSpent;
        
        // Available = total - reserve - spent
        const availableBudget = totalBudget - reserveAmount - totalSpent;
        
        setDashboardBudget({
          total_budget: totalBudget,
          spent_budget: totalSpent,
          reserve_amount: reserveAmount,
          available_budget: availableBudget,
        });
      } else {
        setDashboardBudget(null);
      }
    } catch (e) { console.error(e); }
  }, [authed]);

  useEffect(() => { if (authed) refreshAll(); }, [authed, refreshAll]);

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const r = await axios.get(`${API}/api/alerts`);
        setAlertsData(r.data?.items || []);
      } catch (e) { console.error(e); }
    };
    if (authed) fetchAlerts();
  }, [authed]);

  const deepLinkEntity = searchParams.get('entity') ? JSON.parse(decodeURIComponent(searchParams.get('entity'))) : null;

  // ═══════════════════════════════════════════════════════════════════════════
  //  PROTECTED KPI CONSUMER — single source of truth is `/api/dashboard/stats`.
  //  Do NOT re-derive these from the client-side `guests` array; the backend's
  //  `_compute_guest_kpi_snapshot()` enforces the additive source-of-truth model.
  //  Regression: /app/backend/tests/test_dashboard_kpi_integrity.py
  //  Docs:       /app/memory/KPI_INTEGRITY.md
  // ═══════════════════════════════════════════════════════════════════════════
  const _rsvpStatus = dashStats?.guest_statistics?.rsvp_status || {};
  const _attending = dashStats?.guest_statistics?.attending_breakdown || {};
  const rsvpStats = {
    total_invited: _rsvpStatus.total_invited ?? guests.length,
    yes: _rsvpStatus.yes ?? 0,
    no: _rsvpStatus.no ?? 0,
    pending: _rsvpStatus.pending ?? 0,
    adults_attending: _attending.adults ?? 0,
    children_attending: _attending.children ?? 0,
    unknown_age_attending: _attending.unknown_age ?? 0,
    total_attending: _attending.total_attending ?? 0,
    // Integrity signal — UI surfaces a banner if .passed is false
    integrity_check: dashStats?.guest_statistics?.integrity_check ?? { passed: true, failures: [] },
    integrity_checked_at: dashStatsCheckedAt,
    // Operator-only KPI drift detector. Hidden entirely when has_drift === false.
    drift_check: dashStats?.guest_statistics?.drift_check ?? { has_drift: false, offenders: [] },
    // Step 2 — invite-funnel summary (party/invite-level only).
    invite_funnel: dashStats?.invite_funnel ?? null,
  };

  const minimumTarget = dashStats?.minimum_target || 100;
  const targetPercent = minimumTarget > 0 ? Math.min(Math.round((rsvpStats.yes / minimumTarget) * 100), 100) : 83;

  const budgetData = {
    target: dashStats?.budget_target || 20000,
    committed: dashStats?.budget_committed || 13500,
    paid: dashStats?.budget_paid || 6500,
    balanceDue: Math.max(0, (dashStats?.budget_committed || 13500) - (dashStats?.budget_paid || 6500)),
  };

  const goToNotices = (ids) => {
    setSelectedIds(ids);
    setActiveTab(communicationsHubEnabled ? 'communications' : 'notices');
  };

  // Phase 2 — bidirectional navigation primitives.
  // navigateToModule: from Event Readiness into Vendors / Finance / etc.
  const navigateToModule = useCallback((module, entity_id) => {
    if (!module) return;
    // Event Readiness routes comms actions (RSVP reminder / invitation / follow-up /
    // thank-you) to 'messaging'. When the NEW Communications module is enabled, send
    // those to the live 'communications' surface — it reads the prefill on mount and
    // opens Create — instead of dead-ending in the legacy MessagingTab. When the new
    // module is OFF, legacy MessagingTab keeps handling the prefill exactly as before.
    const target = (commsIntelligenceEnabled && ['messaging', 'notices', 'comments', 'mailbox'].includes(module))
      ? 'communications'
      : module;
    setFocusEntity({ module: target, entity_id: entity_id || null });
    setActiveTab(target);
  }, [commsIntelligenceEnabled]);
  // navigateToReadiness: from a module page back into Event Readiness with
  // an op focused (halo applied via existing focusOpId mechanism).
  const navigateToReadiness = useCallback((op_id) => {
    setFocusEntity({ module: 'event-readiness', op_id: op_id || null });
    setActiveTab('event-readiness');
  }, []);
  const handleAttentionNavigate = (type, entity) => { /* navigation logic */ };
  const handleResolveDuplicate = (a, b) => { setMergeGuests({ a, b }); };
  
  // Handle sidebar item click with tab mapping
  const handleSidebarClick = (itemId) => {
    const mappedTab = tabMapping[itemId] || itemId;
    setActiveTab(mappedTab);
  };
  
  // Title edit handlers
  const handleTitleEdit = () => {
    setTempTitle(dashboardTitle);
    setIsEditingTitle(true);
  };
  
  const handleTitleSave = () => {
    setDashboardTitle(tempTitle);
    setIsEditingTitle(false);
  };

  // ==============================================
  // LOGIN SCREEN
  // ==============================================
  if (!authed) {
    return (
      <div data-testid="admin-login" style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...stitchedBgStyle,
      }}>
        <div style={{
          ...darkPanelStyle,
          padding: '48px 40px',
          maxWidth: 400,
          width: '90%',
          textAlign: 'center',
        }}>
          <GoldDustOverlay intense />
          <NoiseOverlay />
          <div style={{ position: 'relative', zIndex: 2 }}>
            <div style={{ marginBottom: 28, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <img src="/envelope-dark.png" alt="M-Vitations" style={{ width: 200, height: 'auto' }} />
            </div>
            <h2 style={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: 24,
              fontWeight: 400,
              color: '#F5F5F5',
              marginBottom: 8,
            }}>Admin Access</h2>
            <p style={{ fontSize: 14, color: '#aaa', marginBottom: 28 }}>Enter the admin password to continue.</p>
            {pwErr && (
              <div style={{
                background: 'rgba(220,53,69,0.15)',
                border: '1px solid rgba(220,53,69,0.3)',
                borderRadius: 8,
                padding: '12px 16px',
                marginBottom: 20,
                fontSize: 14,
                color: theme.statusError,
              }}>{pwErr}</div>
            )}
            <input
              data-testid="admin-password-input"
              type="password"
              placeholder="Password"
              value={pw}
              onChange={e => { setPw(e.target.value); setPwErr(''); }}
              onKeyDown={e => e.key === 'Enter' && handleLogin()}
              style={{
                width: '100%',
                padding: '16px 18px',
                borderRadius: 8,
                border: '1px solid #444',
                background: '#0F0F0F',
                color: '#eee',
                fontSize: 16,
                marginBottom: 20,
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
            <button
              data-testid="admin-login-btn"
              onClick={handleLogin}
              style={{
                width: '100%',
                padding: '16px',
                borderRadius: 8,
                background: goldGradientShine,
                color: '#1A1A1A',
                border: 'none',
                fontSize: 16,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.3)',
              }}
            >Login</button>
          </div>
        </div>
      </div>
    );
  }

  // ==============================================
  // MOBILE LAYOUT - Global quilted background
  // ==============================================
  if (isMobile) {
    return (
      <div
        data-testid="admin-layout-mode"
        data-layout-mode={MOBILE_LIGHT_ENABLED ? 'mobile-light' : 'mobile-legacy'}
        style={{ minHeight: '100vh', ...globalQuiltedBgStyle, paddingBottom: 80 }}>
        {/* Mobile Header - Transparent to show quilt */}
        <div style={{
          backgroundColor: 'transparent',
          padding: '20px 20px 18px',
          textAlign: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 6 }}>
            <img src={LOGO_URL_SMALL} alt="" style={{ width: 44, height: 'auto' }} />
            <span style={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontStyle: 'italic',
              fontSize: 26,
              background: goldGradientShine,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '0.01em',
            }}>M-Vitations</span>
          </div>
          <p style={{
            fontSize: 13,
            color: '#777',
            letterSpacing: '0.06em',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            margin: 0,
          }}>More than just an invite. Elegant. Organized.</p>
        </div>

        {/* Mobile Menu - Black quilted leather texture */}
        {mobileMenuOpen && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1000 }} onClick={() => setMobileMenuOpen(false)}>
            <div style={{
              ...blackQuiltedLeatherStyle,
              width: '85%',
              maxWidth: 300,
              height: '100%',
              borderRadius: 0,
              padding: '24px 20px',
              overflowY: 'auto',
              borderRight: `2px solid ${GOLD.highlight}`,
              boxShadow: '4px 0 20px rgba(0,0,0,0.5)',
            }} onClick={e => e.stopPropagation()}>
              <GoldDustOverlay intense />
              <div style={{ position: 'relative', zIndex: 2 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 30 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <img src={LOGO_URL_SMALL} alt="" style={{ width: 32 }} />
                    <span style={{
                      fontFamily: 'Georgia, serif',
                      fontStyle: 'italic',
                      fontSize: 18,
                      background: goldGradient,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}>M-Vitations</span>
                  </div>
                  <button onClick={() => setMobileMenuOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8 }}>
                    <X size={24} color="#999" />
                  </button>
                </div>
                {allSidebarItems.map(item => {
                  const Icon = item.icon;
                  const isActive = activeTab === item.id || (item.id === 'alerts' && activeTab === 'attention');
                  return (
                    <button
                      key={item.id}
                      onClick={() => { setActiveTab(item.id === 'alerts' ? 'attention' : navToTab(item.id)); setMobileMenuOpen(false); }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 14,
                        width: '100%',
                        padding: '14px 16px',
                        marginBottom: 4,
                        borderRadius: 8,
                        background: isActive ? 'rgba(212, 170, 105, 0.15)' : 'transparent',
                        border: isActive ? '1px solid rgba(212, 170, 105, 0.3)' : '1px solid transparent',
                        cursor: 'pointer',
                        color: isActive ? GOLD.highlight : '#b0b0b0',
                        fontSize: 16,
                        fontWeight: isActive ? 600 : 400,
                      }}
                    >
                      <Icon size={22} />
                      {item.label}
                      {item.id === 'alerts' && <GoldBadge count={dashStats?.attention_needed || 3} />}
                    </button>
                  );
                })}
                <button
                  onClick={() => { clearAdminSession(); navigate('/'); }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    width: '100%',
                    padding: '14px 16px',
                    marginTop: 24,
                    borderRadius: 8,
                    background: 'transparent',
                    border: '1px solid #444',
                    cursor: 'pointer',
                    color: '#999',
                    fontSize: 15,
                  }}
                >
                  <LogOut size={18} /> Logout
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Mobile Content */}
        {activeTab === 'dashboard' ? (
          <div style={{ padding: 16 }}>
            {/* KPI Card */}
            <div style={{ ...darkPanelStyle, padding: 24, marginBottom: 16 }}>
              <GoldDustOverlay intense />
              <NoiseOverlay />
              <div style={{ position: 'relative', zIndex: 2 }}>
                <div style={{ display: 'flex', justifyContent: 'space-around', marginBottom: 24 }}>
                  {[
                    { value: rsvpStats.total_invited, label: 'Invited' },
                    { value: rsvpStats.yes, label: 'Yes' },
                    { value: rsvpStats.pending, label: 'Pending' },
                    { value: rsvpStats.no, label: 'Declining' },
                  ].map((item, i) => (
                    <div key={i} style={{ textAlign: 'center' }}>
                      <div style={{
                        width: 72,
                        height: 72,
                        borderRadius: '50%',
                        background: `linear-gradient(145deg, rgba(40,40,40,1) 0%, rgba(25,25,25,1) 100%)`,
                        border: '3px solid transparent',
                        backgroundImage: `linear-gradient(145deg, #282828, #191919), ${goldGradientShine}`,
                        backgroundOrigin: 'border-box',
                        backgroundClip: 'padding-box, border-box',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto',
                        boxShadow: `0 0 8px rgba(212, 170, 105, 0.4), inset 0 2px 4px rgba(0,0,0,0.5)`,
                      }}>
                        <span style={{ fontSize: 24, fontWeight: 700, color: '#fff', lineHeight: 1 }}>{item.value}</span>
                        <span style={{ fontSize: 9, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 2 }}>{item.label}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <span style={{ fontSize: 15, color: '#bbb', fontWeight: 500 }}>Minimum Target:</span>
                  <span style={{
                    fontSize: 22,
                    fontWeight: 700,
                    background: goldGradientShine,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}>{targetPercent}%</span>
                </div>
                <GoldProgressBar percent={targetPercent} height={12} dark />
              </div>
            </div>

            {/* Alerts Card */}
            <div style={{ ...darkPanelStyle, padding: 24, marginBottom: 16 }}>
              <GoldDustOverlay intense />
              <NoiseOverlay />
              <div style={{ position: 'relative', zIndex: 2 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                  <span style={{ fontSize: 20, fontWeight: 600, color: '#fff' }}>Alerts</span>
                  <GoldBadge count={dashStats?.attention_needed || 3} large />
                </div>
                {['Vendor payment due', 'Missing WalleR informant', 'RSVP deadline approaching'].map((alert, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                    <span style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: goldGradient,
                      boxShadow: '0 0 4px rgba(212,170,105,0.5)',
                    }} />
                    <span style={{ fontSize: 15, color: '#ddd' }}>{alertsData[i]?.display_message || alert}</span>
                  </div>
                ))}
                <button
                  onClick={() => setActiveTab('attention')}
                  style={{
                    width: '100%',
                    marginTop: 16,
                    padding: '14px',
                    borderRadius: 8,
                    border: `2px solid ${GOLD.highlight}`,
                    background: 'rgba(212, 170, 105, 0.1)',
                    color: GOLD.highlight,
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >View All Alerts</button>
              </div>
            </div>

            {/* Budget Overview */}
            <div style={{ ...darkPanelStyle, padding: 24 }}>
              <GoldDustOverlay intense />
              <NoiseOverlay />
              <div style={{ position: 'relative', zIndex: 2 }}>
                <div style={{ fontSize: 20, fontWeight: 600, color: '#fff', marginBottom: 20 }}>Budget Overview</div>
                {[
                  { label: 'Budget Target', value: budgetData.target },
                  { label: 'Committed', value: budgetData.committed },
                  { label: 'Paid', value: budgetData.paid },
                  { label: 'Balance Due', value: budgetData.balanceDue },
                ].map((item, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '12px 0',
                    borderBottom: i < 3 ? '1px solid rgba(255,255,255,0.1)' : 'none',
                  }}>
                    <span style={{ fontSize: 15, color: '#bbb' }}>{item.label}</span>
                    <span style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>${item.value.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ padding: 16 }}>
            {activeTab === 'guests' && (
              <GuestListTab 
                guests={guests} 
                setGuests={setGuests} 
                refresh={refreshAll} 
                onGoToNotices={goToNotices} 
                deepLinkEntity={deepLinkEntity}
                focusGuestId={focusGuestId}
                onFocusGuestConsumed={() => setFocusGuestId(null)}
                eventConfig={{ guest_journey_config: journeyConfig }}
                onSaveJourneyConfig={handleSaveJourneyConfig}
                onNavigateToTab={(tab, options) => {
                  const nextTab = communicationsHubEnabled && (tab === 'messaging' || tab === 'comments' || tab === 'mailbox' || tab === 'notices')
                    ? 'communications'
                    : navToTab(tab);
                  setActiveTab(nextTab);
                  if (options?.subtab && nextTab === 'messaging') {
                    setMessagingSubtab(options.subtab);
                  }
                }}
                onImportGuests={async (rows) => {
                  const res = await importGuests(rows);
                  if (res) refreshAll();
                  return { added: res.created || 0, skipped: res.errors?.length || 0, duplicates: res.duplicates_flagged || 0 };
                }}
                onAddGuest={async (data) => {
                  const res = await addGuest(data);
                  if (res) refreshAll();
                  return res;
                }}
                onUpdateGuest={async (id, data) => {
                  const res = await updateGuest(id, data);
                  if (res) refreshAll();
                  return res;
                }}
                onUpdateRsvp={async (id, status) => {
                  const res = await updateGuest(id, { rsvp_status: status });
                  if (res) refreshAll();
                  return res;
                }}
                onDeleteGuest={async (id) => {
                  const res = await deleteGuest(id);
                  if (res) refreshAll();
                  return res;
                }}
              />
            )}
            {activeTab === 'vendors' && <VendorsTab deepLinkEntity={deepLinkEntity} onVendorChange={refreshAll} focusEntity={focusEntity?.module === 'vendors' ? focusEntity : null} onNavigateToReadiness={navigateToReadiness} />}
            {/* Pass 8 / M6 — mobile visibility wiring. MessagingTab was
                rendered in the desktop branch but missing here on mobile,
                so any deep link or Milo proposal routing to messaging on
                a mobile session landed on a blank panel. Mirrors desktop
                line 2140 exactly — same prop list, same component. */}
            {activeTab === 'messaging' && <MessagingTab selectedIds={selectedIds} guests={guests} refresh={refreshAll} initialTab={messagingSubtab} integrityCheck={rsvpStats.integrity_check} />}
            {activeTab === 'communications' && (commsIntelligenceEnabled
              ? <CommunicationsIntelligence guests={guests} messages={messages} onMessagesChanged={refreshAll} />
              : <CommunicationsHub guests={guests} messages={messages} onMessagesChanged={refreshAll} />)}
            {activeTab === 'event-readiness' && eventReadinessEnabled && <EventReadinessPage onNavigateToModule={navigateToModule} focusEntity={focusEntity?.module === 'event-readiness' ? focusEntity : null} />}
            {activeTab === 'event-program' && eventProgramEnabled && <EventProgramPage />}
            {activeTab === 'seating' && autoSeatingEnabled && <AutoSeatingPage />}
            {activeTab === 'intelligence-notebook' && intelligenceNotebookEnabled && (
              <Suspense fallback={null}>
                <IntelligenceNotebookPage onBackToMenu={() => setActiveTab(eventReadinessEnabled ? 'event-readiness' : 'dashboard')} />
              </Suspense>
            )}
            {activeTab === 'video-guestbook' && videoGuestbookEnabled && (
              <Suspense fallback={null}><VideoGuestbookAdmin /></Suspense>
            )}
            {(activeTab === 'attention' || activeTab === 'alerts') && <AttentionTab onNavigate={handleAttentionNavigate} onResolveDuplicate={handleResolveDuplicate} />}
            {activeTab === 'notices' && <SendNoticesTab selectedIds={selectedIds} guests={guests} refresh={refreshAll} />}
            {activeTab === 'history' && <SentHistoryTab />}
            {activeTab === 'comments' && <WebCommentsTab onStatsUpdate={refreshAll} />}
            {activeTab === 'oscar' && <AITab onNavigate={(tab, options) => {
              setActiveTab(tab);
              if (options?.subtab && tab === 'messaging') setMessagingSubtab(options.subtab);
            }} />}
            {activeTab === 'upload' && <UploadTab refresh={refreshAll} />}
            {activeTab === 'settings' && <SettingsTab eventConfig={eventConfig} onSaveIdentity={handleSaveIdentity} />}
            {activeTab === 'protection' && <DataProtectionTab />}
            {activeTab === 'calendar' && (
              <CalendarIntelligenceTab
                vendors={vendors}
                financeEntries={financeEntries}
                eventConfig={eventConfig}
                onNavigateToTab={(tab) => setActiveTab(tab)}
              />
            )}
          </div>
        )}

        {/* Mobile Bottom Nav - Black quilted leather */}
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          ...blackQuiltedLeatherStyle,
          borderTop: `2px solid ${GOLD.highlight}`,
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          padding: '12px 0 16px',
          zIndex: 100,
        }}>
          <button onClick={() => setMobileMenuOpen(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8 }}>
            <Menu size={26} color={theme.accentPrimary} />
          </button>
          {mobileNavItems.map(item => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: 8 }}
              >
                <Icon size={24} color={isActive ? GOLD.highlight : '#777'} />
                <span style={{ fontSize: 11, color: isActive ? GOLD.highlight : '#777', fontWeight: isActive ? 600 : 400 }}>{item.label}</span>
              </button>
            );
          })}
          <button
            onClick={() => setActiveTab('upload')}
            style={{
              background: goldGradientShine,
              border: 'none',
              borderRadius: 14,
              width: 50,
              height: 50,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 6px 20px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.3)',
            }}
          >
            <Plus size={26} color="#1A1A1A" strokeWidth={2.5} />
          </button>
          <button
            onClick={() => setMoreMenuOpen(true)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: 8 }}
          >
            <MoreHorizontal size={24} color="#777" />
            <span style={{ fontSize: 11, color: '#777' }}>More</span>
          </button>
        </div>

        {/* More Menu */}
        {moreMenuOpen && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'flex-end' }} onClick={() => setMoreMenuOpen(false)}>
            <div style={{
              ...blackQuiltedLeatherStyle,
              width: '100%',
              borderRadius: '20px 20px 0 0',
              padding: '20px 20px 32px',
              maxHeight: '60vh',
              overflowY: 'auto',
              borderTop: `2px solid ${GOLD.highlight}`,
            }} onClick={e => e.stopPropagation()}>
              <div style={{ width: 48, height: 5, background: GOLD.highlight, borderRadius: 3, margin: '0 auto 20px' }} />
              <div style={{ fontSize: 18, fontWeight: 600, color: GOLD.highlight, marginBottom: 16 }}>More Options</div>
              {allSidebarItems.filter(item => !mobileNavItems.find(n => n.id === item.id)).map(item => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => { setActiveTab(item.id === 'alerts' ? 'attention' : navToTab(item.id)); setMoreMenuOpen(false); }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                      width: '100%',
                      padding: '14px 16px',
                      marginBottom: 4,
                      borderRadius: 8,
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: theme.accentPrimary,
                      fontSize: 16,
                    }}
                  >
                    <Icon size={22} color={theme.accentPrimary} />{item.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {showTrash && <TrashModal onClose={() => { setShowTrash(false); refreshAll(); }} onRestore={refreshAll} />}
        {activeModal && <DashboardModals type={activeModal} onClose={() => { setActiveModal(null); refreshAll(); }} onNavigate={(tab) => { setActiveModal(null); setActiveTab(tab); }} />}
        {mergeGuests && <GuestMergeModal guestA={mergeGuests.a} guestB={mergeGuests.b} onClose={() => setMergeGuests(null)} onMerged={() => { setMergeGuests(null); refreshAll(); }} />}
      </div>
    );
  }

  // ==============================================
  // DESKTOP LAYOUT - Refined from v1.1-admin-uplift
  // ==============================================

  // Toggle group collapse
  const toggleGroup = (group) => {
    setCollapsedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  // Map nav item to actual tab
  const navToTab = (navId) => {
    const mapping = {
      'dashboard': 'dashboard',
      'guests': 'guests',
      'messaging': communicationsHubEnabled ? 'communications' : 'messaging',
      'mailbox': communicationsHubEnabled ? 'communications' : 'comments',     // Phase 2: Guest Mailbox routes to Hub only while the Hub flag is enabled.
      'comments': communicationsHubEnabled ? 'communications' : 'comments',
      'vendors': 'vendors',
      'finance': 'finance',
      'calendar': 'calendar',
      'event-program': 'event-program',
      'intelligence-notebook': 'intelligence-notebook',
      'video-guestbook': 'video-guestbook',
      'seating': 'seating',
      'analytics': 'analytics',
      'oscar': 'oscar',          // Phase 0 (Feb 2026): real Oscar admin shell. No longer misroutes to 'comments'.
      'security': 'protection',
      'settings': 'settings',
      'trash': null, // Special handling
    };
    return mapping[navId] || navId;
  };

  // Handle nav click
  const handleNavClick = (navId) => {
    if (navId === 'trash') {
      setShowTrash(true);
    } else {
      setActiveTab(navToTab(navId));
    }
  };

  // Get badge count for nav item
  const getNavBadge = (navId) => {
    if (navId === 'guests') return guests.length > 0 ? guests.length : null;
    if (navId === 'messaging') return messages.filter(m => m.status === 'unread').length || null;
    if (navId === 'trash') return deletedCount || null;
    return null;
  };

  const getNavLabel = (item) => {
    if (communicationsHubEnabled && item.id === 'messaging') return 'Communications';
    return item.label;
  };

  const shouldShowNavItem = (item) => {
    // Event Readiness is feature-flagged (Phase 0).
    if (!eventReadinessEnabled && item.id === 'event-readiness') return false;
    // Event Program is feature-flagged (default OFF).
    if (!eventProgramEnabled && item.id === 'event-program') return false;
    if (!autoSeatingEnabled && item.id === 'seating') return false;
    // Intelligence Notebook is feature-flagged (default OFF).
    if (!intelligenceNotebookEnabled && item.id === 'intelligence-notebook') return false;
    // Video Guestbook admin gallery is feature-flagged (default OFF).
    if (!videoGuestbookEnabled && item.id === 'video-guestbook') return false;
    // Communications Inbox owns Guest Mailbox while the Hub is enabled.
    return !(communicationsHubEnabled && item.id === 'mailbox');
  };

  // Sidebar grouping: when the Intelligence Notebook flag is ON, present the
  // host's exact 4-section spec (CORE / PLANNING / INTELLIGENCE / CONTROL) with
  // the new tab under PLANNING. When OFF, the live production nav is untouched.
  const activeNavGroups = intelligenceNotebookEnabled ? navGroupsNotebook : navGroups;
  
  // Check if this is preview environment
  const isPreviewEnv = API && API.includes('preview.emergentagent.com');
  
  return (
    <div data-testid="admin-layout-mode" data-layout-mode="desktop" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundImage: 'url(/admin-bg.png)', backgroundSize: 'cover', backgroundRepeat: 'no-repeat', backgroundAttachment: 'fixed', backgroundPosition: 'center center', backgroundColor: '#F7F3EC' }}>
      {/* Preview Environment Banner — STRONG WARNING (Feb 2026)
          Visible only on the preview hostname. Tells the agent and the operator
          unmistakably that data changes here do NOT propagate to live. */}
      {isPreviewEnv && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          minHeight: 64,
          padding: '8px 16px',
          background: 'repeating-linear-gradient(135deg, #B91C1C 0 24px, #991B1B 24px 48px)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 14,
          fontSize: 14,
          fontWeight: 800,
          letterSpacing: '0.5px',
          zIndex: 9999,
          textTransform: 'uppercase',
          textAlign: 'center',
          borderBottom: '3px solid #FCD34D',
          boxShadow: '0 2px 12px rgba(185,28,28,0.45)',
          textShadow: '0 1px 2px rgba(0,0,0,0.4)',
        }} data-testid="preview-environment-banner">
          <span style={{ fontSize: 22 }}>⚠️</span>
          <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.3 }}>
            <span style={{ fontSize: 15, fontWeight: 900 }}>PREVIEW ENVIRONMENT — NOT YOUR LIVE DATA</span>
            <span style={{ fontSize: 12, fontWeight: 700, opacity: 0.95 }}>
              DO NOT APPLY REAL FIXES HERE. Changes on this page DO NOT reach 80yearsofconnection.com.
            </span>
          </span>
          <span style={{ fontSize: 22 }}>⚠️</span>
        </div>
      )}
      {/* ==============================================
          LUXURY HEADER - High-end Modern Design (FIXED)
          ============================================== */}
      <header className="admin-header" style={{
        height: 80,
        background: '#000000',
        borderBottom: 'none',
        display: 'flex',
        alignItems: 'center',
        padding: '0 32px',
        position: 'fixed',
        top: isPreviewEnv ? 64 : 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        boxShadow: '0 6px 28px rgba(0,0,0,0.30), 0 2px 6px rgba(0,0,0,0.20)',
      }}>
        {/* Gold accent line at bottom */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 2,
          background: 'linear-gradient(90deg, transparent 0%, #6B5018 8%, #C6A048 30%, #F0D080 50%, #C6A048 70%, #6B5018 92%, transparent 100%)',
          pointerEvents: 'none',
        }} />

        {/* M-Vitations Logo - positioned top-left above sidebar */}
        <div style={{
          width: 240,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginLeft: -32,
          paddingLeft: 20,
        }}>
          <img 
            src="/envelope-dark.png"
            alt="M-Vitations"
            style={{ height: 72, objectFit: 'contain' }}
          />
        </div>

        {/* Left section - Event info (above Dashboard title) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {/* Event Switcher Card */}
          <button 
            className="header-event-switcher" 
            onClick={() => setActiveTab('settings')}
            data-testid="event-switcher-btn"
            title="Edit event settings"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '10px 18px',
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(185, 139, 31, 0.35)',
              borderRadius: 10,
              cursor: 'pointer',
              transition: 'all 250ms cubic-bezier(0.25, 1, 0.5, 1)',
            }}>
            <Calendar size={15} color="#f8e7a0" />
            <span style={{
              fontSize: 14,
              fontWeight: 500,
              color: '#FFFFFF',
              maxWidth: 200,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              letterSpacing: '0.2px',
            }}>{dashboardTitle}</span>
            <ChevronDown size={14} color="rgba(255,255,255,0.5)" />
          </button>

          {/* Days Until Event - Premium Badge */}
          <div className="header-days-card" style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '8px 16px',
            background: 'linear-gradient(135deg, rgba(185,139,31,0.15) 0%, rgba(130,97,16,0.08) 100%)',
            border: '1px solid rgba(185, 139, 31, 0.40)',
            borderRadius: 10,
            position: 'relative',
            overflow: 'hidden',
          }}>
            {/* Shimmer effect */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, transparent 0%, rgba(248,231,160,0.05) 50%, transparent 100%)',
              pointerEvents: 'none',
            }} />
            <span style={{ 
              fontSize: 22, 
              fontWeight: 700, 
              background: 'linear-gradient(135deg, #f8e7a0 0%, #b98b1f 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              lineHeight: 1,
              letterSpacing: '-0.5px',
            }}>{daysUntilEvent}</span>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <span style={{ fontSize: 9, fontWeight: 600, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '1px' }}>Days</span>
              <span style={{ fontSize: 9, fontWeight: 600, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '1px' }}>Left</span>
            </div>
          </div>
        </div>

        {/* Center section — Ask Milo Anything pill (v6.2 production wire)
            replaces the prior gray search field; search functionality
            now lives inside the command palette it opens. */}
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', padding: '0 40px' }}>
          <AskMiloPalette
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            searchResults={searchResults}
            onNavigate={setActiveTab}
            onFocusGuest={setFocusGuestId}
          />
        </div>
        
        {/* Date/Time Indicator */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          marginLeft: 24,
          marginRight: 8,
        }}>
          <span style={{
            fontSize: 13,
            fontWeight: 500,
            color: 'rgba(255,255,255,0.85)',
            letterSpacing: '0.3px',
          }}>
            {currentTime.toLocaleDateString('en-US', { 
              weekday: 'short',
              month: 'short', 
              day: 'numeric',
              year: 'numeric'
            })}
          </span>
          <span style={{
            fontSize: 11,
            color: 'rgba(212,168,83,0.9)',
            fontWeight: 500,
            marginTop: 2,
          }}>
            {currentTime.toLocaleTimeString('en-US', { 
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            })}
          </span>
        </div>

        {/* Right section - Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Elegant divider */}
          <div style={{
            width: 1,
            height: 28,
            background: 'linear-gradient(180deg, transparent 0%, rgba(185,139,31,0.3) 50%, transparent 100%)',
          }} />

          {/* Host Dashboard QR — scan to open /admin/host-dashboard on phone.
              Same 40x40 footprint as Help/Notifications so it does not shift
              any other header element. */}
          <HostDashboardQrButton />

          {/* Help Button */}
          <button 
            onClick={() => toast.info('Help & Documentation', {
              description: 'For help, contact support or view the user guide.',
              duration: 4000,
            })}
            data-testid="header-help-btn"
            style={{
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 10,
              border: '1px solid rgba(185, 139, 31, 0.25)',
              background: 'rgba(255,255,255,0.06)',
              cursor: 'pointer',
              transition: 'all 200ms ease',
            }}>
            <HelpCircle size={17} color="rgba(255,255,255,0.6)" />
          </button>

          {/* Notifications */}
          <div style={{ position: 'relative' }}>
            <button 
              onClick={() => {
                if (!notifOpen) {
                  axios.get(`${API}/api/attention-items`).then(r => setNotifItems((r.data.items || []).filter(i => i.status !== 'resolved'))).catch(() => {});
                }
                setNotifOpen(v => !v);
              }}
              data-testid="header-notifications-btn"
              style={{
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 10,
                border: '1px solid rgba(185, 139, 31, 0.25)',
                background: 'rgba(255,255,255,0.06)',
                cursor: 'pointer',
                transition: 'all 200ms ease',
                position: 'relative',
              }}>
              <Bell size={17} color="rgba(255,255,255,0.6)" />
              {dashStats?.attention_needed > 0 && (
                <div style={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  minWidth: 18,
                  height: 18,
                  borderRadius: 9,
                  background: 'linear-gradient(135deg, #b98b1f 0%, #826110 100%)',
                  border: '2px solid #1F1C18',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 9,
                  fontWeight: 700,
                  color: '#FFFFFF',
                }}>
                  {dashStats.attention_needed > 9 ? '9+' : dashStats.attention_needed}
                </div>
              )}
            </button>
            {notifOpen && (
              <div data-testid="notif-dropdown" style={{
                position: 'absolute', top: 48, right: 0, width: 360,
                background: 'rgba(30,26,20,0.96)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(198,167,90,0.30)', borderRadius: 14,
                boxShadow: '0 20px 60px rgba(0,0,0,0.40)', zIndex: 200,
                maxHeight: 420, overflowY: 'auto',
              }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 16px', borderBottom:'1px solid rgba(198,167,90,0.18)' }}>
                  <span style={{ fontSize:13, fontWeight:700, color:'#F5F0E8', letterSpacing:'0.5px' }}>Notifications</span>
                  <button onClick={() => { setNotifOpen(false); setActiveTab('attention'); }} style={{ background:'none', border:'none', cursor:'pointer', fontSize:11, color:'rgba(198,167,90,0.80)', fontWeight:600 }}>View All</button>
                </div>
                {notifItems.length === 0 ? (
                  <div style={{ padding:28, textAlign:'center', color:'rgba(255,255,255,0.45)', fontSize:13 }}>No active notifications</div>
                ) : (
                  notifItems.slice(0, 8).map(item => (
                    <div key={item.id} data-testid={`notif-item-${item.id}`} style={{
                      display:'flex', alignItems:'flex-start', gap:10, padding:'12px 16px',
                      borderBottom:'1px solid rgba(198,167,90,0.10)',
                      transition:'background 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background='rgba(198,167,90,0.08)'}
                    onMouseLeave={e => e.currentTarget.style.background='transparent'}
                    >
                      <div style={{ width:8, height:8, borderRadius:4, marginTop:5, flexShrink:0,
                        background: item.severity === 'red' ? '#D14B4B' : '#C8A64B' }} />
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:12, fontWeight:500, color:'#F5F0E8', lineHeight:1.5, wordBreak:'break-word' }}>
                          {item.title || item.display_message}
                        </div>
                        <div style={{ fontSize:10, color:'rgba(255,255,255,0.35)', marginTop:3 }}>
                          {item.reason_code?.replace(/_/g,' ')}
                        </div>
                      </div>
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            await axios.delete(`${API}/api/attention-items/${item.id}`);
                            setNotifItems(prev => prev.filter(i => i.id !== item.id));
                            refreshAll();
                          } catch {}
                        }}
                        data-testid={`notif-dismiss-${item.id}`}
                        title="Dismiss"
                        style={{ background:'none', border:'none', cursor:'pointer', padding:4, flexShrink:0 }}
                      >
                        <X size={14} color="rgba(255,255,255,0.35)" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Elegant divider */}
          <div style={{
            width: 1,
            height: 28,
            background: 'linear-gradient(180deg, transparent 0%, rgba(185,139,31,0.3) 50%, transparent 100%)',
            margin: '0 6px',
          }} />

          {/* Avatar / Account */}
          <button 
            onClick={() => {
              if (window.confirm('Log out of the admin panel?')) {
                clearAdminSession();
                setIsLoggedIn(false);
                toast.success('Logged out successfully');
              }
            }}
            data-testid="header-avatar-btn"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '6px 14px 6px 6px',
              borderRadius: 28,
              border: '1px solid rgba(185, 139, 31, 0.30)',
              background: 'rgba(255,255,255,0.06)',
              cursor: 'pointer',
              transition: 'all 200ms ease',
            }}>
            <div style={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #f8e7a0 0%, #b98b1f 50%, #826110 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 'inset 0px 2px 4px rgba(255, 255, 255, 0.3), 0 2px 4px rgba(0,0,0,0.2)',
            }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#1F1C18' }}>{lastNameOf(eventConfig.host_name) ? lastNameOf(eventConfig.host_name)[0].toUpperCase() : (firstNameOf(eventConfig.host_name) ? firstNameOf(eventConfig.host_name)[0].toUpperCase() : 'A')}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              {/* Logged-in account: show only the person's name (no time-of-day
                  greeting, no "Administrator" label) per host preference. */}
              <span style={{ fontSize: 13, fontWeight: 600, color: '#FFFFFF', lineHeight: 1.2, letterSpacing: '0.2px' }}>{hostGreetingName(eventConfig) || 'Account'}</span>
            </div>
            <ChevronDown size={14} color="rgba(255,255,255,0.4)" />
          </button>
        </div>
      </header>

      {/* Main Layout - with top margin for fixed header */}
      <div style={{ display: 'flex', flex: 1, marginTop: isPreviewEnv ? 144 : 80 }}>
        {/* ==============================================
            REFINED SIDEBAR - Classic Theme (FIXED)
            ============================================== */}
        <aside className="admin-sidebar" style={{
          width: 240,
          backgroundImage: 'url(/panel-bg.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          borderRight: `1px solid ${theme.sidebarBorder}`,
          display: 'flex',
          flexDirection: 'column',
          position: 'fixed',
          top: isPreviewEnv ? 144 : 80,
          left: 0,
          bottom: 0,
          overflow: 'hidden',
          zIndex: 999,
        }}>
          {/* Right edge gold accent */}
          <div style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: 3,
            height: '100%',
            background: 'linear-gradient(180deg, #E6C579 0%, #C6A75A 50%, #9F8B5D 100%)',
            opacity: 0.6,
          }} />

          {/* Brand spacer with gold border — logo is in header above */}
          <div style={{
            borderBottom: '1px solid rgba(198,167,90,0.20)',
          }} />

          {/* Navigation */}
          <nav style={{ flex: 1, padding: '16px 12px', overflowY: 'auto', position: 'relative', zIndex: 2 }}>
            {Object.entries(activeNavGroups).map(([groupKey, group]) => (
              <div key={groupKey} style={{ marginBottom: 8 }}>
                {/* Group Header */}
                <button
                  onClick={() => toggleGroup(groupKey)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    padding: '10px 12px',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    borderRadius: 8,
                    transition: 'background-color 150ms ease',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ChevronRight 
                      size={12} 
                      color={theme.sidebarSectionLabel} 
                      style={{
                        transition: 'transform 200ms ease',
                        transform: collapsedGroups[groupKey] ? 'rotate(0deg)' : 'rotate(90deg)',
                      }}
                    />
                    <span style={{
                      fontSize: 10,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.8px',
                      color: theme.sidebarSectionLabel,
                    }}>{group.label}</span>
                  </div>
                </button>

                {/* Group Divider - gold gradient */}
                <div style={{
                  height: 1,
                  margin: '0 12px 8px',
                  background: theme.sidebarSectionDivider,
                }} />

                {/* Group Items */}
                {!collapsedGroups[groupKey] && group.items.filter(shouldShowNavItem).map((item) => {
                  const Icon = item.lucideIcon;
                  const isActive = activeTab === navToTab(item.id) || (item.id === 'trash' && showTrash);
                  const badge = getNavBadge(item.id);
                  const navLabel = getNavLabel(item);

                  return (
                    <button
                      key={item.id}
                      data-testid={`nav-${item.id}`}
                      onClick={() => handleNavClick(item.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        width: '100%',
                        // tighter item rhythm in the restructured (flag-on) nav; prod nav unchanged
                        padding: intelligenceNotebookEnabled ? '7px 14px' : '12px 14px',
                        marginBottom: intelligenceNotebookEnabled ? 0 : 2,
                        borderRadius: 10,
                        border: isActive ? '1px solid rgba(198,167,90,0.45)' : '1px solid transparent',
                        background: isActive 
                          ? 'linear-gradient(135deg, rgba(198,167,90,0.16) 0%, rgba(198,167,90,0.08) 100%)' 
                          : 'transparent',
                        backdropFilter: isActive ? 'blur(8px)' : 'none',
                        WebkitBackdropFilter: isActive ? 'blur(8px)' : 'none',
                        cursor: 'pointer',
                        transition: 'all 200ms ease',
                        position: 'relative',
                        textAlign: 'left',
                        boxShadow: isActive ? '0 4px 14px rgba(0,0,0,0.20), inset 0 1px 0 rgba(198,167,90,0.18), inset 0 -1px 0 rgba(0,0,0,0.15)' : 'none',
                        borderLeft: isActive ? '2.5px solid #C6A75A' : '2.5px solid transparent',
                      }}
                    >
                      {/* Icon */}
                      <div style={{
                        width: 32,
                        height: 32,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 8,
                        background: isActive 
                          ? 'rgba(198, 167, 90, 0.20)' 
                          : 'rgba(255, 255, 255, 0.08)',
                        border: isActive ? '1px solid rgba(198,167,90,0.30)' : '1px solid rgba(255,255,255,0.08)',
                        transition: 'all 200ms ease',
                      }}>
                        <Icon size={17} color={isActive ? '#C6A75A' : theme.sidebarText} />
                      </div>

                      {/* Label */}
                      <span style={{
                        fontSize: 13,
                        fontWeight: isActive ? 600 : 500,
                        color: isActive ? theme.sidebarActiveText : theme.sidebarText,
                        transition: 'color 180ms ease',
                      }}>{navLabel}</span>

                      {/* Badge */}
                      {badge && (
                        <div style={{
                          marginLeft: 'auto',
                          minWidth: 20,
                          height: 20,
                          padding: '0 6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 10,
                          background: theme.sidebarBadgeBg,
                          fontSize: 10,
                          fontWeight: 700,
                          color: theme.sidebarBadgeText,
                        }}>{badge > 99 ? '99+' : badge}</div>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>

          {/* Sidebar Footer - positioned just below navigation */}
          <div style={{
            padding: 16,
            borderTop: `1px solid ${theme.sidebarBorder}`,
            position: 'relative',
            zIndex: 2,
            marginTop: 8,
          }}>
            {/* Milo — centered video (replaces the old "Last Worked On" card).
               The source mp4 is 16:9 with ~22% black pillarbars each side; a square
               container + object-fit:cover crops the side bars exactly, and the slight
               scale trims the minor top/bottom dark slivers. Poster shows until the
               video can play; reduced-motion users get the still poster. */}
            <div style={{
              marginBottom: 12,
              display: 'flex',
              justifyContent: 'center',
            }}>
              <div style={{
                width: '100%',
                maxWidth: 168,
                aspectRatio: '1 / 1',
                borderRadius: 14,
                overflow: 'hidden',
                background: 'rgba(255, 255, 255, 0.10)',
                border: `1px solid ${theme.sidebarBorder}`,
                boxShadow: '0 8px 22px -14px rgba(0,0,0,0.6)',
              }}>
                <video
                  src="/milo.mp4"
                  poster="/milo-footer-poster.png"
                  autoPlay
                  muted
                  loop
                  playsInline
                  aria-label="Milo, your AI assistant"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center',
                    transform: 'scale(1.08)',
                    display: 'block',
                  }}
                />
              </div>
            </div>

            {/* Utility Links */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
            }}>
              <span style={{ fontSize: 10, color: theme.sidebarText, cursor: 'pointer' }}>Privacy</span>
              <span style={{ fontSize: 10, color: theme.sidebarText }}>•</span>
              <span style={{ fontSize: 10, color: theme.sidebarText, cursor: 'pointer' }}>Terms</span>
            </div>
          </div>
        </aside>

        {/* ==============================================
            MAIN CONTENT AREA
            ============================================== */}
        <main className="admin-main main-dashboard-area" style={{
          flex: 1,
          marginLeft: 240,
          background: theme.bgContent,
          overflowY: 'auto',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 'calc(100vh - 72px)',
        }}>
          {/* Subtle radial accents */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(ellipse at 0% 0%, rgba(198, 167, 90, 0.04) 0%, transparent 50%), radial-gradient(ellipse at 100% 100%, rgba(198, 167, 90, 0.03) 0%, transparent 50%)',
            pointerEvents: 'none',
            zIndex: 0,
          }} />

          <div style={{ flex: 1, position: 'relative', zIndex: 1, minWidth: activeTab === 'intelligence-notebook' ? 0 : 1680 }}>
          {activeTab === 'dashboard' ? (
            <div style={{ padding: 28 }}>
              <DashboardTab
                guestStats={{
                  // PROTECTED KPI CONSUMER — values come straight from canonical rsvpStats
                  // which is sourced from /api/dashboard/stats (see `_compute_guest_kpi_snapshot`).
                  // DO NOT re-derive these from the client-side `guests` array.
                  total: rsvpStats.total_invited,
                  pending: rsvpStats.pending,
                  sent: guests.filter(g => g.invitation_status === 'sent').length || 0,
                  failed: guests.filter(g => g.invitation_status === 'failed').length || 0,
                  rsvp_yes: rsvpStats.yes,
                  rsvp_no: rsvpStats.no,
                  adults_attending: rsvpStats.adults_attending,
                  children_attending: rsvpStats.children_attending,
                  unknown_age_attending: rsvpStats.unknown_age_attending,
                  total_attending: rsvpStats.total_attending,
                  adults_yes: rsvpStats.adults_attending,
                  children_yes: rsvpStats.children_attending,
                  official_invitations_sent: officialInvitationsSent,
                  integrity_check: rsvpStats.integrity_check,
                  integrity_checked_at: rsvpStats.integrity_checked_at,
                  drift_check: rsvpStats.drift_check,
                  invite_funnel: rsvpStats.invite_funnel,
                }}
                messages={messages}
                onDeleteMessage={(id) => setMessages(prev => prev.filter(m => m.id !== id))}
                vendors={vendors}
                budget={dashboardBudget}
                liquidity={(() => {
                  const cashReceived = sponsors.reduce((sum, s) => sum + (s.amount_received || s.received || 0), 0);
                  const vendorCommitted = vendors.reduce((sum, v) => sum + (v.contract_total || 0), 0);
                  const expenseCommitted = financeEntries.filter(e => e.type !== 'sponsor' && !e.cancelled).reduce((sum, e) => sum + (e.contract_amount || 0), 0);
                  return { cash_received: cashReceived, committed: vendorCommitted + expenseCommitted };
                })()}
                eventConfig={{
                  id: '1',
                  name: dashboardTitle,
                  date: eventConfig.date,
                  location: eventConfig.location || 'Passaic, NJ',
                  honoree: eventConfig.honoree || 'Our Beloved',
                  minimum_guest_target: eventConfig.minimum_guest_target,
                  adult_plate_cost: eventConfig.adult_plate_cost,
                  child_plate_cost: eventConfig.child_plate_cost,
                  // Vendor-meal settings — the plate-cost tile reads these; omitting them here
                  // made the tile show "not charged (enable in Settings)" even when enabled.
                  vendor_meal_rate: eventConfig.vendor_meal_rate,
                  vendor_meals_enabled: eventConfig.vendor_meals_enabled,
                }}
                onNavigate={(tab) => {
                  const nextTab = communicationsHubEnabled && (tab === 'notices' || tab === 'messaging' || tab === 'comments')
                    ? 'communications'
                    : navToTab(tab);
                  setActiveTab(nextTab);
                }}
              />
            </div>
          ) : (
            <div style={{ padding: 28 }}>
              {activeTab === 'guests' && (
                <GuestListTab 
                  guests={guests} 
                  setGuests={setGuests} 
                  refresh={refreshAll} 
                  onGoToNotices={goToNotices} 
                  deepLinkEntity={deepLinkEntity}
                  focusGuestId={focusGuestId}
                  onFocusGuestConsumed={() => setFocusGuestId(null)}
                  eventConfig={{ guest_journey_config: journeyConfig }}
                  integrityCheck={rsvpStats.integrity_check}
                  onSaveJourneyConfig={handleSaveJourneyConfig}
                  onNavigateToTab={(tab, options) => {
                    const nextTab = communicationsHubEnabled && (tab === 'messaging' || tab === 'comments' || tab === 'mailbox' || tab === 'notices')
                      ? 'communications'
                      : navToTab(tab);
                    setActiveTab(nextTab);
                    if (options?.subtab && nextTab === 'messaging') {
                      setMessagingSubtab(options.subtab);
                    }
                  }}
                  onImportGuests={async (rows) => {
                    const res = await importGuests(rows);
                    if (res) refreshAll();
                    return { added: res.created || 0, skipped: res.errors?.length || 0, duplicates: res.duplicates_flagged || 0 };
                  }}
                  onAddGuest={async (data) => {
                    const res = await addGuest(data);
                    if (res) refreshAll();
                    return res;
                  }}
                  onUpdateGuest={async (id, data) => {
                    const res = await updateGuest(id, data);
                    if (res) refreshAll();
                    return res;
                  }}
                  onUpdateRsvp={async (id, status) => {
                    const res = await updateGuest(id, { rsvp_status: status });
                    if (res) refreshAll();
                    return res;
                  }}
                  onDeleteGuest={async (id) => {
                    const res = await deleteGuest(id);
                    if (res) refreshAll();
                    return res;
                  }}
                />
              )}
              {activeTab === 'vendors' && <VendorsTab deepLinkEntity={deepLinkEntity} onVendorChange={refreshAll} focusEntity={focusEntity?.module === 'vendors' ? focusEntity : null} onNavigateToReadiness={navigateToReadiness} />}
              {(activeTab === 'attention' || activeTab === 'alerts') && <AttentionTab onNavigate={handleAttentionNavigate} onResolveDuplicate={handleResolveDuplicate} />}
              {activeTab === 'messaging' && <MessagingTab selectedIds={selectedIds} guests={guests} refresh={refreshAll} initialTab={messagingSubtab} integrityCheck={rsvpStats.integrity_check} />}
              {activeTab === 'communications' && (commsIntelligenceEnabled
              ? <CommunicationsIntelligence guests={guests} messages={messages} onMessagesChanged={refreshAll} />
              : <CommunicationsHub guests={guests} messages={messages} onMessagesChanged={refreshAll} />)}
              {activeTab === 'event-readiness' && eventReadinessEnabled && <EventReadinessPage onNavigateToModule={navigateToModule} focusEntity={focusEntity?.module === 'event-readiness' ? focusEntity : null} />}
            {activeTab === 'event-program' && eventProgramEnabled && <EventProgramPage />}
            {activeTab === 'seating' && autoSeatingEnabled && <AutoSeatingPage />}
            {activeTab === 'intelligence-notebook' && intelligenceNotebookEnabled && (
              <Suspense fallback={null}>
                <IntelligenceNotebookPage onBackToMenu={() => setActiveTab(eventReadinessEnabled ? 'event-readiness' : 'dashboard')} />
              </Suspense>
            )}
            {activeTab === 'video-guestbook' && videoGuestbookEnabled && (
              <Suspense fallback={null}><VideoGuestbookAdmin /></Suspense>
            )}
              {activeTab === 'finance' && <FinanceTab focusEntity={focusEntity?.module === 'finance' ? focusEntity : null} onNavigateToReadiness={navigateToReadiness} />}
              {activeTab === 'notices' && <SendNoticesTab selectedIds={selectedIds} guests={guests} refresh={refreshAll} integrityCheck={rsvpStats.integrity_check} />}
              {activeTab === 'history' && <SentHistoryTab />}
              {activeTab === 'comments' && <WebCommentsTab onStatsUpdate={refreshAll} />}
              {activeTab === 'oscar' && <AITab onNavigate={(tab, options) => {
                setActiveTab(tab);
                if (options?.subtab && tab === 'messaging') setMessagingSubtab(options.subtab);
              }} />}
              {activeTab === 'upload' && <UploadTab refresh={refreshAll} />}
              {activeTab === 'settings' && <SettingsTab eventConfig={eventConfig} onSaveIdentity={handleSaveIdentity} />}
              {activeTab === 'protection' && <DataProtectionTab />}
              {activeTab === 'calendar' && (
                <CalendarIntelligenceTab
                  vendors={vendors}
                  financeEntries={financeEntries}
                  eventConfig={eventConfig}
                  onNavigateToTab={(tab) => setActiveTab(tab)}
                />
              )}
              {activeTab === 'analytics' && (
                <AnalyticsTab
                  guestStats={{
                    total: guests.length || rsvpStats.total_invited,
                    sent: guests.length || rsvpStats.total_invited,
                    pending: rsvpStats.pending,
                    failed: 0,
                    rsvp_yes: rsvpStats.yes,
                    rsvp_no: rsvpStats.no,
                    adults_attending: rsvpStats.adults_attending || rsvpStats.yes,
                    children_attending: rsvpStats.children_attending || 0,
                  }}
                  emailStats={null}
                  budget={dashboardBudget}
                  vendors={vendors}
                  guests={guests}
                  eventConfig={eventConfig}
                />
              )}
            </div>
          )}
          </div>

          {/* Admin Footer */}
          <footer style={{
            background: 'linear-gradient(180deg, #1E1A14 0%, #161210 100%)',
            borderTop: 'none',
            padding: '14px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'relative',
            zIndex: 1,
            overflow: 'hidden',
          }}>
            {/* Gold top hairline */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: 'linear-gradient(90deg, transparent 0%, #6B5018 8%, #C6A048 30%, #F0D080 50%, #C6A048 70%, #6B5018 92%, transparent 100%)', pointerEvents: 'none' }} />
            {/* Specular highlight below the line */}
            <div style={{ position: 'absolute', top: 1, left: 0, right: 0, height: 12, background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, transparent 100%)', pointerEvents: 'none' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, position: 'relative', zIndex: 1 }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: 'rgba(198,167,90,0.85)' }}>M-Vitations</span>
              <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}> · Secure · Elegant · Organized</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, position: 'relative', zIndex: 1 }}>
              {['Privacy', 'Terms', 'Support'].map(label => (
                <span key={label} style={{ fontSize: 10, color: 'rgba(255,255,255,0.30)', cursor: 'pointer', transition: 'color 150ms' }}
                  onMouseEnter={e => e.target.style.color = 'rgba(198,167,90,0.85)'}
                  onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.30)'}>{label}</span>
              ))}
            </div>
          </footer>
        </main>
      </div>

      {showTrash && <TrashModal onClose={() => { setShowTrash(false); refreshAll(); }} onRestore={refreshAll} />}
      {activeModal && <DashboardModals type={activeModal} onClose={() => { setActiveModal(null); refreshAll(); }} onNavigate={(tab) => { setActiveModal(null); setActiveTab(tab); }} />}
      {mergeGuests && <GuestMergeModal guestA={mergeGuests.a} guestB={mergeGuests.b} onClose={() => setMergeGuests(null)} onMerged={() => { setMergeGuests(null); refreshAll(); }} />}

      {/* v6.3 — Quick Add modal, opened via ⌘⇧A from any tab. */}
      {quickAddOpen && eventReadinessEnabled && (
        <QuickAddComposer
          mode="modal"
          onCreate={apiCreateOperation}
          onParse={apiParseQuickAdd}
          onClose={() => setQuickAddOpen(false)}
        />
      )}

      {/* Toast notifications for save feedback */}
      <AdminToaster />
    </div>
  );
};

export default AdminPage;
